/*
 * DO NOT DELETE — base-info and page-design tags are consumed by project-snapshot tooling for quick page overview. Always update them to reflect actual page content.
 * <base-info>
 * Description: 用户修改登录密码的页面
 * Style referenceFiles:
 * Design for: Mobile
 * </base-info>
 * <page-design>
 * ## Features & Interactions
 * - 输入旧密码
 * - 输入新密码
 * - 确认修改
 * - 导航: 返回设置页 (back)
 *
 * ## Basic Layout
 * Standard: Header + Main
 *
 * ## Reuse Components
 * 无
 *
 * ## Page Layout
 * `flex flex-col h-screen bg-gray-50`
 *
 * **Header**: nav-bar - [返回(icon)] - [修改密码] - [none]
 *
 * **Main** `flex-1 p-4`:
 * 1. 表单区: `flex flex-col gap-4 bg-white p-4 rounded-xl shadow-sm`
 *    - 旧密码输入: Label "旧密码" + Input `type="password"`
 *    - 新密码输入: Label "新密码" + Input `type="password"`
 * 2. 操作区: `mt-6`
 *    - 确认按钮 `w-full py-3 bg-blue-500 text-white rounded-lg font-medium`
 *
 * ## Mock Data
 * 输入框为空状态。
 *
 * ## Reference
 * - canvas/[Feature] Auth/feature-brief.md
 * </page-design>
 */

import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { toast } from '@/components/function/base/Toaster'
import * as ChangePasswordStore from '../../stores/ChangePasswordStore'

function ChangePassword() {
  const [oldPassword, setOldPassword] = useAtom(ChangePasswordStore.oldPasswordAtom)
  const [newPassword, setNewPassword] = useAtom(ChangePasswordStore.newPasswordAtom)
  const isSubmitting = useAtomValue(ChangePasswordStore.isSubmittingAtom)
  const submit = useSetAtom(ChangePasswordStore.submitAtom)
  const navigate = useNavigate()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    void (async () => {
      const result = await submit()
      if (result) {
        toast.success('密码修改成功')
        void navigate(-1)
      }
    })()
  }

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: '#12141D' }}>
      {/* Header */}
      <div className="flex-none pt-12 pb-4 px-6 flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: '#1C1F2E' }}
        >
          <Icon icon="lucide:arrow-left" className="w-5 h-5 text-gray-400" />
        </button>
        <h1 className="text-xl font-bold text-white">修改密码</h1>
      </div>

      {/* Main Content */}
      <main className="flex-1 px-6 pt-4">
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <div className="rounded-2xl p-5 flex flex-col gap-4" style={{ backgroundColor: '#1C1F2E' }}>
            <div>
              <label className="text-xs text-gray-400 mb-2 block font-medium">旧密码</label>
              <input
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                placeholder="请输入旧密码"
                className="w-full px-4 py-3.5 rounded-xl text-white text-sm font-medium outline-none placeholder:text-gray-600 placeholder:font-normal"
                style={{ backgroundColor: '#12141D', border: '1.5px solid #2A2D3A' }}
                autoComplete="current-password"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-2 block font-medium">新密码</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="请输入新密码"
                className="w-full px-4 py-3.5 rounded-xl text-white text-sm font-medium outline-none placeholder:text-gray-600 placeholder:font-normal"
                style={{ backgroundColor: '#12141D', border: '1.5px solid #2A2D3A' }}
                autoComplete="new-password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-4 rounded-xl text-white text-lg font-bold tracking-wide transition-opacity disabled:opacity-60 border-b-[4px] active:border-b-0 active:translate-y-[4px]"
            style={{ backgroundColor: '#10B981', borderColor: '#059669' }}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <Icon icon="mdi:loading" className="animate-spin" style={{ fontSize: '20px' }} />
                提交中...
              </span>
            ) : (
              '确认修改'
            )}
          </button>
        </form>
      </main>
    </div>
  )
}

export default ChangePassword
